import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { RawIntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import {
  unstable_HistoryRouter as HistoryRouter,
  HistoryRouterProps,
} from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { ErrorBoundary } from 'react-error-boundary';
import intl from 'i18n';
import store from 'store';
import theme from 'styles/theme';
import PageLoader from 'components/PageLoader';
import UnhandledErrorScreen from 'components/UnhandledErrorScreen';
import { NavigationPromptProvider } from 'contexts';
import App from './App';
import isDev from './helpers/env/isDev';
import { recoverFromVitePreloadError } from './helpers/errors';
import initApi from './api/addUnauthorizedInterceptor';

window.addEventListener('vite:preloadError', (event) => {
  recoverFromVitePreloadError(event, {
    storage: window.sessionStorage,
    reload: () => window.location.reload(),
  });
});

initApi();

const history =
  createBrowserHistory() as unknown as HistoryRouterProps['history'];

if (!isDev) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: import.meta.env.VITE_COMMIT_OR_TAG,
    environment: import.meta.env.VITE_PROJECT_ENV,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

root.render(
  <RawIntlProvider value={intl}>
    <Provider store={store}>
      <HistoryRouter history={history}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={theme}>
            <CssBaseline>
              <Suspense fallback={<PageLoader open />}>
                <ErrorBoundary
                  FallbackComponent={UnhandledErrorScreen}
                  onReset={() => window.location.reload()}
                >
                  <NavigationPromptProvider>
                    <App />
                  </NavigationPromptProvider>
                </ErrorBoundary>
              </Suspense>
            </CssBaseline>
          </ThemeProvider>
        </LocalizationProvider>
      </HistoryRouter>
    </Provider>
  </RawIntlProvider>,
);
